// Floating Pill skin — a floating, frosted-glass control bar inspired by the
// macOS QuickTime player. Everything is scoped under .skin-floating-pill and
// only restyles plyr's existing controls (no markup changes). The played
// scrubber uses the user's branding colour via --plyr-color-main.

// The controls that ride the right edge of the pill. Named once — the order
// rule and the right-edge push below both build their selectors from it.
$right-cluster: ".plyr__control[data-plyr='captions'], .plyr__menu, .plyr__control[data-plyr='pip'], .plyr__control[data-plyr='airplay'], .plyr__control[data-plyr='fullscreen']";

// Tokens sit on .plyr--video, not the wrapper: the audio player reads the same
// plyr variables, so an audio preset carrying this slug would otherwise get a
// transparent control background with white-on-white icons.
.skin-floating-pill.plyr--video,
.skin-floating-pill .plyr--video {
  // theming tokens plyr reads — tweak the look without touching selectors
  --plyr-video-controls-background: transparent; // kill the base gradient
  --plyr-video-control-color: rgba(255, 255, 255, 0.9);
  --plyr-video-control-color-hover: #fff;
  --plyr-video-control-background-hover: rgba(255, 255, 255, 0.14);
  --plyr-control-spacing: 8px;
  --plyr-control-icon-size: 16px;
  --plyr-control-radius: 9999px;
  --plyr-range-track-height: 3px;
  --plyr-range-thumb-height: 12px;
  --plyr-range-thumb-background: #fff;
  --plyr-range-thumb-shadow: none;
  // played portion follows the user's brand colour
  --plyr-range-fill-background: var(--plyr-color-main);
  --plyr-video-range-track-background: rgba(255, 255, 255, 0.25);
  --plyr-video-progress-buffered-background: rgba(255, 255, 255, 0.4);
  // frosted popover for the settings menu + tooltips
  --plyr-menu-background: rgba(30, 30, 32, 0.82);
  --plyr-menu-color: rgba(255, 255, 255, 0.92);
  --plyr-menu-radius: 12px;
  // the white dot is the whole marker. plyr also fills a ring behind it (brand
  // colour by default), which on dark glass reads as a grey blob under the dot.
  // kept in the layout, just invisible — it carries the label indent.
  --plyr-control-toggle-checked-background: transparent;
  --plyr-font-size-menu: 13px;
  --plyr-tooltip-background: rgba(30, 30, 32, 0.85);
  --plyr-tooltip-color: #fff;
  --plyr-tooltip-radius: 8px;
}

.skin-floating-pill {
  // scoped to the wrapper class so we out-specify the base control styles
  &.presto-player__wrapper {
    // the floating frosted pill itself
    .plyr--video .plyr__controls {
      position: absolute;
      left: 50%;
      right: auto;
      bottom: 16px;
      transform: translateX(-50%);
      // bounded floating capsule (a pill, not a full-width bar)
      width: min(calc(100% - 24px), 420px);
      display: flex;
      flex-wrap: wrap; // lets the scrubber drop to its own row
      align-items: center;
      justify-content: flex-start; // auto margins place each cluster (see order rules)
      padding: clamp(6px, 1.6%, 8px) clamp(10px, 3%, 14px);
      background: rgba(28, 28, 30, 0.72);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: clamp(14px, 4%, 20px);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      backdrop-filter: blur(24px) saturate(180%); // dark smoked glass
      box-shadow:
        0 8px 28px rgba(0, 0, 0, 0.38),
        // soft drop shadow
        inset 0 1px 0 rgba(255, 255, 255, 0.14); // faint top highlight edge
      transition:
        opacity 0.3s ease,
        transform 0.3s ease;
    }

    // volume left · transport centred · right group; times + scrubber on the row below
    .plyr__controls__item {
      order: 0;
      margin: 0; // clusters are placed by auto margins / absolute, not plyr's per-item margins
      // restrict the transition to visual props so plyr's `transition: all` here
      // doesn't interfere with the control re-ordering (the `order` rules below)
      transition-property: background-color, color, box-shadow, opacity, transform;
    }
    // plyr puts margin-right:auto on the dom-first item — reset it so the left
    // cluster hugs the left edge instead of being shoved across
    .plyr__controls .plyr__controls__item:first-child {
      margin: 0;
    }
    // volume hugs the left; its trailing auto margin pushes the right group to
    // the far edge (the transport is out of flow, so it can't do the pushing).
    // volume stays monochrome white — only the scrubber carries brand colour
    .plyr__volume {
      order: 2;
      margin-inline-end: auto;
      --plyr-range-fill-background: rgba(255, 255, 255, 0.9);
    }
    // transport is pulled out of flow and pinned to the pill's true centre, so
    // play stays dead-centre no matter how wide the volume slider or right group
    // are. --pp-transport-gap is the spacing between adjacent button centres.
    .plyr__control[data-plyr='rewind'],
    .plyr__control[data-plyr='play']:not(.plyr__control--overlaid),
    .plyr__control[data-plyr='fast-forward'] {
      position: absolute;
      top: 8px;
      left: 50%;
    }
    .plyr__control[data-plyr='rewind'] {
      order: 5;
      transform: translateX(calc(-50% - var(--pp-transport-gap, 34px)));
    }
    .plyr__control[data-plyr='play']:not(.plyr__control--overlaid) {
      order: 6;
      transform: translateX(-50%) scale(1.1); // play sits a touch larger

      // The play glyph is a right-pointing triangle, so its visual mass sits
      // left of the box centre and a geometrically centred one reads as
      // off-centre inside the round hover background. Nudge it back a pixel.
      // The pause glyph is symmetrical and is deliberately left alone.
      .icon--not-pressed {
        margin-left: 1px;
      }
    }
    .plyr__control[data-plyr='fast-forward'] {
      order: 7;
      transform: translateX(calc(-50% + var(--pp-transport-gap, 34px)));
    }
    // right group — settings, fullscreen, captions etc. ride the right edge,
    // pushed there by the volume's trailing auto margin while volume is shown
    #{$right-cluster} {
      order: 16;
    }
    // but plyr drops .plyr__volume entirely when a preset turns off both mute
    // and volume, and that push goes with it. so whichever member of the group
    // comes first (any subset can be hidden too) pushes the group right itself
    .plyr__controls .plyr__controls__item:is(#{$right-cluster}):not(:where(#{$right-cluster}) ~ *) {
      margin-inline-start: auto;
    }
    // the transport is out of flow, so a preset that hides every in-flow
    // first-row control (volume + the whole right group) collapses that row
    // and drops the play button onto the scrubber. this zero-width strut
    // keeps the first line as tall as a control button — plyr's own height
    // formula: icon + padding (0.7 × spacing) both sides — whenever a
    // transport button is present
    .plyr--video
      .plyr__controls:has(.plyr__control[data-plyr='play']:not(.plyr__control--overlaid), .plyr__control[data-plyr='rewind'], .plyr__control[data-plyr='fast-forward'])::after {
      content: '';
      order: 0;
      width: 0;
      height: calc(var(--plyr-control-icon-size) + var(--plyr-control-padding, calc(var(--plyr-control-spacing) * 0.7)) * 2);
    }

    // forced flex line break — everything ordered after it wraps to the bottom row
    .plyr--video .plyr__controls::before {
      content: '';
      order: 18;
      flex: 1 0 100%;
      height: 0;
      margin: 0;
    }
    // bottom row: current time · scrubber (grows to fill) · duration
    .plyr__controls__item.plyr__progress__container {
      order: 21;
      flex: 1 1 auto;
      min-width: 0;
      margin: 2px 0 0;
    }
    .plyr__progress {
      display: flex;
      align-items: center;
    }
    .plyr__progress__buffer {
      height: 3px;
    }

    // thin track with a thumb that only appears on hover (cross-browser)
    .plyr__progress input[type='range'] {
      &::-webkit-slider-runnable-track {
        height: 3px;
        transition: height 0.25s ease;
      }
      &::-moz-range-track {
        height: 3px;
        transition: height 0.25s ease;
      }
      &::-webkit-slider-thumb {
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.25s ease;
      }
      &::-moz-range-thumb {
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.25s ease;
      }
    }
    // reveal the thumb on hover AND keyboard focus so tab-and-seek users can see
    // the playback handle (focus-within also covers touch, which has no hover)
    .plyr__progress__container:hover input[type='range'],
    .plyr__progress__container:focus-within input[type='range'] {
      &::-webkit-slider-thumb {
        visibility: visible;
        opacity: 1;
      }
      &::-moz-range-thumb {
        visibility: visible;
        opacity: 1;
      }
    }

    // times flank the scrubber on the bottom row — current on the left, and
    // duration (when a preset shows it) on the right
    .plyr__time {
      order: 20;
      margin: 2px 0 0;
      font-size: 12px;
      opacity: 0.85;
      white-space: nowrap;
    }
    .plyr__time--duration {
      order: 22;
    }

    // big overlaid play button as a frosted circle — fades in/out
    button.plyr__control.plyr__control--overlaid {
      padding: 22px;
      border-radius: 9999px;
      // hold the true centre: plyr can swap this transform for a scale on some
      // paused states, which drops the -50% offset and shifts the button down/right
      transform: translate(-50%, -50%) !important;
      background: rgba(28, 28, 30, 0.62);
      -webkit-backdrop-filter: blur(18px) saturate(160%);
      backdrop-filter: blur(18px) saturate(160%);
      box-shadow:
        0 4px 18px rgba(0, 0, 0, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.14);
      transition:
        opacity 0.3s ease,
        background-color 0.2s ease,
        box-shadow 0.2s ease;
    }
    // gentle lift on hover
    button.plyr__control.plyr__control--overlaid:hover {
      background: rgba(44, 44, 48, 0.78);
      box-shadow:
        0 6px 24px rgba(0, 0, 0, 0.45),
        inset 0 1px 0 rgba(255, 255, 255, 0.22);
    }

    // frost the settings popover to match the pill
    .plyr__menu__container {
      -webkit-backdrop-filter: blur(24px) saturate(160%);
      backdrop-filter: blur(24px) saturate(160%);
      // the pill floats at the bottom, so the menu opens upward. the block wrapper
      // clips overflow (for its border-radius) and lives outside our shadow scope,
      // so a tall menu (e.g. the 8-step speed list) would be cut off at the video's
      // top edge. cap the height and let it scroll instead of overflowing the frame.
      //
      // !important because menu-sizing.js sets an inline max-height of
      // (player height - 48px) on every skin, which on a normal player is tall
      // enough to run the speed list from the top edge down under the pill.
      // An inline style can only be beaten this way.
      max-height: 200px !important;
      overflow-y: auto;
    }

    // Speed alone lists eight options, which pushes the menu tall enough to
    // cover most of a short video. Tighten the rows so the list fits the frame
    // before the cap above has to scroll it.
    .plyr__menu__container .plyr__control[role='menuitemradio'] {
      margin-top: 0;
      padding-block: 3px;
    }

    // plyr left-aligns the tooltip on the first two DOM children of the control
    // bar, on the assumption that they hug its left edge. Here the transport is
    // pulled out of flow and centred while its DOM order is untouched, so rewind
    // and play still match that rule and their tooltips hang off to the right.
    // Put the centred tooltip back for the whole transport. Deliberately not
    // touching :last-child — fullscreen really is at the right edge, and its
    // right-aligned tooltip is what keeps it inside the pill.
    .plyr__controls > .plyr__control:is([data-plyr='rewind'], [data-plyr='play'], [data-plyr='fast-forward']) {
      .plyr__tooltip {
        left: 50%;
        transform: translate(-50%, 10px) scale(0.8);
        transform-origin: 50% 100%;

        &::before {
          left: 50%;
        }
      }

      &:hover .plyr__tooltip,
      &.plyr__tab-focus .plyr__tooltip {
        transform: translate(-50%, 0) scale(1);
      }
    }

    // clear, on-brand keyboard focus ring over the translucent glass
    .plyr__control:focus-visible {
      outline: 2px solid var(--plyr-color-main, #fff);
      outline-offset: 2px;
    }

    // slide the pill down out of view while controls are idle
    .plyr--video.plyr--hide-controls .plyr__controls {
      opacity: 0;
      transform: translate(-50%, 220%);
      pointer-events: none;
    }

    // keep it compact on small players: tuck the pill nearer the edge, tighten
    // the gaps and shrink the icons/time so the row never feels oversized
    @media (max-width: 480px) {
      .plyr--video .plyr__controls {
        bottom: 10px;
        column-gap: 2px;
        padding: 5px 8px;
        --plyr-control-icon-size: 15px; // smaller icons so the row stays tidy
        --pp-transport-gap: 28px; // tighter centre spacing on small players
      }
      // re-seat the out-of-flow transport for the smaller top padding
      .plyr__control[data-plyr='rewind'],
      .plyr__control[data-plyr='play']:not(.plyr__control--overlaid),
      .plyr__control[data-plyr='fast-forward'] {
        top: 5px;
      }
      // smaller time so labels still clear the scrubber ends
      .plyr__time {
        font-size: 11px;
      }
      // shrink the centre play button to match the tighter bar
      button.plyr__control.plyr__control--overlaid {
        padding: 16px;
      }
    }

    // solid fallback where backdrop-filter isn't supported
    @supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
      .plyr--video .plyr__controls,
      button.plyr__control.plyr__control--overlaid {
        background: rgba(24, 24, 26, 0.92);
      }
    }

    // respect reduced-motion preferences (incl. the overlaid play button)
    @media (prefers-reduced-motion: reduce) {
      .plyr--video .plyr__controls,
      button.plyr__control.plyr__control--overlaid,
      .plyr__progress input[type='range']::-webkit-slider-thumb,
      .plyr__progress input[type='range']::-moz-range-thumb {
        transition: none;
      }
    }
  }
}
